import React from "react";
import { gql } from "@apollo/client";
import { Field } from "react-final-form";
import { FieldRequiredError } from "@syos-music/form";
import isEqual from "lodash.isequal";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { FieldArray } from "react-final-form-arrays";

import {
  styled,
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Backdrop,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as RemoveIcon,
} from "mdi-material-ui";

import FileUpload from "components/common/FileUpload";

import ActionsTooltip from "./ActionsTooltip";
import PreviewImage from "./PreviewImage";
import PreviewAudio from "./PreviewAudio";

const DefaultPreview = React.forwardRef(function DefaultPreview(
  { file, ...props },
  ref
) {
  return (
    <a
      ref={ref}
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {file.name}
    </a>
  );
});

function FilePreview({ renderActions, preview, ...props }) {
  if (typeof preview === "function")
    return (
      <FilePreview
        preview={preview(props.file)}
        renderActions={renderActions}
        {...props}
      />
    );

  let PreviewComp = DefaultPreview;
  if (preview === "image") PreviewComp = PreviewImage;
  if (preview === "audio") PreviewComp = PreviewAudio;

  return (
    <ActionsTooltip
      actions={
        <>
          <ActionsTooltip.Action
            onClick={props.onRemove}
            icon={<RemoveIcon />}
            label="Supprimer"
          />
          <FilePreview.DefaultActions preview={preview} {...props} />
          {renderActions?.({ preview, ...props })}
        </>
      }
    >
      <PreviewComp file={props.file} />
    </ActionsTooltip>
  );
}

FilePreview.DefaultActions = ({ preview, ...props }) => {
  if (typeof preview === "function")
    return (
      <FilePreview.DefaultActions preview={preview(props.file)} {...props} />
    );

  if (preview === "image") return <PreviewImage.DefaultActions {...props} />;
  if (preview === "audio") return <PreviewAudio.DefaultActions {...props} />;

  return null;
};

function FileInputWrapper({
  uploadPath,
  isPublic,
  onUpload,

  label,
  shrinkLabel,
  helperText,

  children,
}) {
  return (
    <FileUpload uploadPath={uploadPath} isPublic={isPublic} onUpload={onUpload}>
      {({ canDrop }) => (
        <div>
          <FormControl sx={{ position: "relative" }}>
            {label && <InputLabel shrink={shrinkLabel}>{label}</InputLabel>}

            <Box
              sx={{
                marginTop: label ? 2 : 0,
                minHeight: 24,
                maxHeight: (t) => `calc(50px + ${t.spacing(2)})`,
              }}
            >
              {children}
            </Box>

            {helperText && <FormHelperText>{helperText}</FormHelperText>}

            <Backdrop open={canDrop} sx={{ position: "absolute" }}>
              <UploadIcon />
            </Backdrop>
          </FormControl>
        </div>
      )}
    </FileUpload>
  );
}

function FileInput({
  label,

  input,
  uploadPath,
  isPublic,
  preview,
  renderActions,

  helperText,
}) {
  const { onChange } = input;

  const handleRemove = React.useCallback(() => onChange(null), [onChange]);

  return (
    <FileInputWrapper
      uploadPath={uploadPath}
      isPublic={isPublic}
      onUpload={onChange}
      label={label}
      shrinkLabel={!!input.value}
      helperText={helperText}
    >
      <FilePreview
        preview={preview}
        renderActions={renderActions}
        file={input.value}
        uploadPath={uploadPath}
        isPublic={isPublic}
        onRemove={handleRemove}
        onChange={onChange}
      />
    </FileInputWrapper>
  );
}

const List = styled(Grid)`
  overflow: auto;
`;

const ListItem = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function MultipleFileInput({
  label,

  fields,
  uploadPath,
  isPublic,
  preview,
  renderActions,

  helperText,
}) {
  return (
    <FileInputWrapper
      uploadPath={uploadPath}
      isPublic={isPublic}
      onUpload={(file) => fields.push(file)}
      label={label}
      shrinkLabel={!!fields.value?.length}
      helperText={helperText}
    >
      <DragDropContext
        onDragEnd={({ source, destination }) => {
          // dropped outside the list
          if (!destination) return;

          fields.move(source.index, destination.index);
        }}
      >
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided) => (
            <List
              container
              spacing={1}
              alignItems="stretch"
              wrap="nowrap"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {fields.map((name, index) => (
                <Draggable
                  key={fields.value[index].id}
                  draggableId={fields.value[index].id}
                  index={index}
                >
                  {(provided) => (
                    <ListItem
                      item
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <FilePreview
                        preview={preview}
                        renderActions={renderActions}
                        uploadPath={uploadPath}
                        isPublic={isPublic}
                        onChange={(file) => fields.update(index, file)}
                        onRemove={() => fields.remove(index)}
                        file={fields.value[index]}
                      />
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </FileInputWrapper>
  );
}

export default function FileField({ multiple, name, ...props }) {
  return multiple ? (
    <FieldArray name={name} isEqual={isEqual}>
      {({ fields, meta }) => (
        <MultipleFileInput fields={fields} meta={meta} {...props} />
      )}
    </FieldArray>
  ) : (
    <Field
      component={FileInput}
      isEqual={isEqual}
      validate={(value) => {
        if (props.required && value == null) return <FieldRequiredError />;
      }}
      name={name}
      {...props}
    />
  );
}

FileField.Action = ActionsTooltip.Action;

FileField.fragments = {
  file: gql`
    fragment FileField_file on File {
      id
      url
      isPublic
      path
      name
      metas {
        alt {
          fr
          en
        }
        legend {
          fr
          en
        }
      }
    }
  `,
};
